import { SelectInput } from '@/components/form';
import { PermissionCategories } from '@/features/settings/constants/permissions';
import { useAppRoleList } from '@/features/users/hooks';
import { Permission } from '@/types/permissions.types';
import { Alert, Card, Checkbox, Col, Collapse, Row, Space } from 'antd';
import React from 'react';

const { Panel } = Collapse;

interface RolePermissionsStepProps {
  selectedRole?: string; // Role ID (dogma_approleid)
  basePermissions: Permission[];
  customPermissions: Permission[];
  onToggleCustomPermission: (permission: Permission) => void;
}

export const RolePermissionsStep: React.FC<RolePermissionsStepProps> = ({
  selectedRole,
  basePermissions,
  customPermissions,
  onToggleCustomPermission,
}) => {
  // Fetch roles from API
  const { data: rolesData, isLoading: isLoadingRoles } = useAppRoleList({
    queryParams: { current: 0, pageSize: 100 },
    enabled: true,
  });

  // Convert API roles to options for SelectInput
  const roleOptions = rolesData?.value?.map((role) => ({
    label: role.dogma_name,
    value: role.dogma_approleid,
  })) || [];

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Role & Permissions</h3>

      <Row gutter={16}>
        <Col xs={24}>
          <SelectInput
            name="role"
            label="User Role"
            placeholder={isLoadingRoles ? "Loading roles..." : "Select role"}
            options={roleOptions}
            loading={isLoadingRoles}
            disabled={isLoadingRoles}
            required
          />
          <p className="text-xs text-gray-600 -mt-3 mb-4">
            Select a role from available custom roles.
          </p>
        </Col>
      </Row>

      {/* Custom Permissions Section */}
      {selectedRole && (() => {
        // Calculate total available permissions across all categories
        const totalAvailablePermissions = PermissionCategories.reduce((count, category) => {
          const availableInCategory = category.permissions.filter(
            (perm) => !basePermissions.includes(perm.id)
          );
          return count + availableInCategory.length;
        }, 0);

        return (
          <div className="mt-4">
            <h4 className="font-semibold mb-2">Custom Permissions</h4>
            <p className="text-sm text-gray-600 mb-3">
              Grant additional permissions beyond the base role
            </p>

            {totalAvailablePermissions === 0 ? (
              <Alert
                message="All Permissions Included"
                description="This role already includes all available permissions in the system. No additional custom permissions can be granted."
                type="info"
                showIcon
              />
            ) : (
              <Collapse accordion>
                {PermissionCategories.map((category) => {
                  // Filter out permissions that are already in base role
                  const availablePermissions = category.permissions.filter(
                    (perm) => !basePermissions.includes(perm.id)
                  );

                  // Skip category if all permissions are base permissions
                  if (availablePermissions.length === 0) {
                    return null;
                  }

                  return (
                    <Panel header={category.name} key={category.id}>
                      <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {availablePermissions.map((perm) => {
                          const isCustom = customPermissions.includes(perm.id);

                          return (
                            <Card
                              key={perm.id}
                              size="small"
                              className={isCustom ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}
                            >
                              <Space align="start" style={{ width: '100%' }}>
                                <Checkbox
                                  checked={isCustom}
                                  onChange={() => onToggleCustomPermission(perm.id)}
                                />
                                <div style={{ flex: 1 }}>
                                  <div className="font-medium text-gray-900">{perm.label}</div>
                                  <div className="text-gray-600 text-xs mt-1">{perm.description}</div>
                                </div>
                              </Space>
                            </Card>
                          );
                        })}
                      </Space>
                    </Panel>
                  );
                })}
              </Collapse>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default RolePermissionsStep;
